import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

import { ModalRenderer } from "./ModalRenderer";
import { INITIAL_MODAL_STATE, modalReducer } from "./reducer";
import type {
  ModalAction,
  ModalActionId,
  ModalPayloadMap,
  ModalState,
} from "./types";

type ModalDispatch = React.Dispatch<ModalAction>;

interface ClaimModalContextValue {
  state: ModalState;
  dispatch: ModalDispatch;
  openModal: <K extends ModalActionId>(
    actionId: K,
    payload: ModalPayloadMap[K]
  ) => void;
  closeModal: () => void;
}

const ClaimModalContext = createContext<ClaimModalContextValue | null>(null);

export function ClaimModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(modalReducer, INITIAL_MODAL_STATE);

  const openModal = useCallback(
    <K extends ModalActionId>(actionId: K, payload: ModalPayloadMap[K]) => {
      dispatch({ type: "OPEN", actionId, payload });
    },
    []
  );

  const closeModal = useCallback(() => {
    dispatch({ type: "CLOSE" });
  }, []);

  const value = useMemo(
    () => ({ state, dispatch, openModal, closeModal }),
    [state, openModal, closeModal]
  );

  return (
    <ClaimModalContext.Provider value={value}>
      {children}
      <ModalRenderer />
    </ClaimModalContext.Provider>
  );
}

export function useClaimModal(): ClaimModalContextValue {
  const ctx = useContext(ClaimModalContext);
  if (ctx == null) {
    throw new Error("useClaimModal must be used within a ClaimModalProvider");
  }
  return ctx;
}
